import { useChatContext } from '@copilotkit/react-ui';
import { Modal, Tag, Input, Button } from 'antd';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { aiCopilotServer } from '@/service/ai-copilot';
import { useAgentContext } from './agent';
import styles from './index.module.scss';

const { TextArea } = Input;

// Rating 枚举定义
enum Rating {
  cancel = 0,    // 取消
  unuseful = 1,  // 点踩
  useful = 2,    // 点赞
}

export interface MessageControlBarProps {
  isCurrentMessage: boolean;
  isLastAssistantMessage: boolean;
  inProgress?: boolean;
  onRegenerate?: (messageId: string) => void,
  onCopy?: (message: string) => void,
  onThumbsUp?: (message: any) => void,
  onThumbsDown?: (message: any) => void,
  message?: any;
}

export const MessageControlBar = (props: MessageControlBarProps) => {
  const { t } = useTranslation();
  const {
    isCurrentMessage,
    isLastAssistantMessage,
    inProgress = false,
    onRegenerate,
    onThumbsDown,
    onThumbsUp,
    message,
  } = props;
  const { icons, labels } = useChatContext();
  const { threadId, setCurrentAgent } = useAgentContext();

  // 反馈状态：使用 Rating 枚举
  const [feedbackStatus, setFeedbackStatus] = useState<Rating>(Rating.useful);

  // 点赞反馈状态
  const [thumbsUpTags, setThumbsUpTags] = useState<string[]>([]);
  const [thumbsUpFeedback, setThumbsUpFeedback] = useState('');
  const [thumbsUpVisible, setThumbsUpVisible] = useState(false);
  const [thumbsUpLoading, setThumbsUpLoading] = useState(false);

  // 点踩反馈状态
  const [thumbsDownTags, setThumbsDownTags] = useState<string[]>([]);
  const [thumbsDownFeedback, setThumbsDownFeedback] = useState('');
  const [thumbsDownVisible, setThumbsDownVisible] = useState(false);
  const [thumbsDownLoading, setThumbsDownLoading] = useState(false);

  // 初始化反馈状态：从消息的 feedback 字段读取
  useEffect(() => {
    if (message?.feedback?.rating !== undefined) {
      setFeedbackStatus(message.feedback.rating);
    } else {
      setFeedbackStatus(Rating.cancel);
    }
  }, [message]);

  // 只有消息渲染完成才显示控制栏
  // 如果是当前消息且正在生成中，则不显示
  if (isCurrentMessage && inProgress) {
    return null;
  }

  const handleRegenerate = () => {
    if (!message.agentId) return;

    setCurrentAgent(message.agentId, () => {
      if (onRegenerate) onRegenerate(message.id);
    });
  };

  // 点赞选项
  const thumbsUpOptions = [t('理解正确'), t('数据准确'), t('回答全面'), t('格式规范')];

  // 点踩选项
  const thumbsDownOptions = [t('理解错误'), t('推理错误'), t('数据不准确'), t('格式错误')];

  // 处理点赞标签点击
  const handleThumbsUpTagClick = (tag: string) => {
    setThumbsUpTags(prev => (
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    ));
  };

  // 处理点踩标签点击
  const handleThumbsDownTagClick = (tag: string) => {
    setThumbsDownTags(prev => (
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    ));
  };

  // 处理点赞确认
  const handleThumbsUpConfirm = async () => {
    if (!message?.id) {
      return;
    }

    // 立即关闭弹窗，提升体验
    setThumbsUpVisible(false);

    setThumbsUpLoading(true);
    try {
      await aiCopilotServer.feedbackMsg({
        sessionId: threadId,
        msgId: message.id,
        feedback: {
          rating: Rating.useful,
          category: thumbsUpTags,
          moreContext: thumbsUpFeedback,
        },
      });

      if (onThumbsUp) {
        onThumbsUp(message.content);
      }

      // 提交成功后重置表单状态
      setThumbsUpTags([]);
      setThumbsUpFeedback('');
    } catch (error) {
      // 提交失败，回滚状态到未选中
      setFeedbackStatus(Rating.cancel);
    } finally {
      setThumbsUpLoading(false);
    }
  };

  // 处理点踩确认
  const handleThumbsDownConfirm = async () => {
    if (!message?.id) {
      return;
    }

    // 立即关闭弹窗，提升体验
    setThumbsDownVisible(false);

    setThumbsDownLoading(true);
    try {
      // 发起异步请求
      await aiCopilotServer.feedbackMsg({
        sessionId: threadId,
        msgId: message.id,
        feedback: {
          rating: Rating.unuseful,
          category: thumbsDownTags,
          moreContext: thumbsDownFeedback,
        },
      });

      if (onThumbsDown) {
        onThumbsDown(message.content);
      }

      // 提交成功后重置表单状态
      setThumbsDownTags([]);
      setThumbsDownFeedback('');
    } catch (error) {
      // 提交失败，回滚状态到未选中
      setFeedbackStatus(Rating.cancel);
    } finally {
      setThumbsDownLoading(false);
    }
  };

  // 处理点赞按钮点击
  const handleThumbsUpClick = async () => {
    // 如果已经是点赞状态，直接取消
    if (feedbackStatus === Rating.useful) {
      if (!message?.id) {
        return;
      }

      // 保存原始状态，用于失败回滚
      const previousStatus = feedbackStatus;

      // 先更新本地状态
      setFeedbackStatus(Rating.cancel);

      try {
        // 再发起异步请求
        await aiCopilotServer.feedbackMsg({
          sessionId: threadId,
          msgId: message.id,
          feedback: {
            rating: Rating.cancel,
            category: [],
            moreContext: '',
          },
        });
      } catch (error) {
        // 提交失败，回滚状态
        setFeedbackStatus(previousStatus);
      }
    } else {
      // 先更新本地状态为点赞
      setFeedbackStatus(Rating.useful);
      // 再打开弹窗
      setThumbsUpVisible(true);
    }
  };

  // 处理点踩按钮点击
  const handleThumbsDownClick = async () => {
    // 如果已经是点踩状态，直接取消
    if (feedbackStatus === Rating.unuseful) {
      if (!message?.id) {
        return;
      }

      // 保存原始状态，用于失败回滚
      const previousStatus = feedbackStatus;

      // 先更新本地状态
      setFeedbackStatus(Rating.cancel);

      try {
        await aiCopilotServer.feedbackMsg({
          sessionId: threadId,
          msgId: message.id,
          feedback: {
            rating: Rating.cancel,
            category: [],
            moreContext: '',
          },
        });
      } catch (error) {
        // 提交失败，回滚状态
        setFeedbackStatus(previousStatus);
      }
    } else {
      // 先更新本地状态为点踩
      setFeedbackStatus(Rating.unuseful);
      // 再打开弹窗
      setThumbsDownVisible(true);
    }
  };

  // 处理点赞取消
  const handleThumbsUpCancel = () => {
    // 用户取消弹窗，回滚状态
    setFeedbackStatus(Rating.cancel);
    setThumbsUpTags([]);
    setThumbsUpFeedback('');
    setThumbsUpVisible(false);
  };

  // 处理点踩取消
  const handleThumbsDownCancel = () => {
    // 用户取消弹窗，回滚状态
    setFeedbackStatus(Rating.cancel);
    setThumbsDownTags([]);
    setThumbsDownFeedback('');
    setThumbsDownVisible(false);
  };

  // 点赞反馈内容
  const thumbsUpContent = (
    <div className={styles.thumbsDownContent}>
      <div className={styles.thumbsTagContainer}>
        {thumbsUpOptions.map(option => (
          <Tag.CheckableTag
            key={option}
            checked={thumbsUpTags.includes(option)}
            onChange={() => handleThumbsUpTagClick(option)}
            className={`${styles.thumbsTag} ${thumbsUpTags.includes(option) ? styles.active : ''}`}
          >
            {option}
          </Tag.CheckableTag>
        ))}
      </div>
      <div style={{ padding: '0 20px 20px 20px' }}>
        <TextArea
          value={thumbsUpFeedback}
          onChange={e => setThumbsUpFeedback(e.target.value)}
          placeholder={t('请写下更多反馈内容')}
          rows={4}
          style={{ resize: 'none', height: 56, borderRadius: 6 }}
        />
      </div>
    </div>
  );

  // 点踩反馈内容
  const thumbsDownContent = (
    <div className={styles.thumbsDownContent}>
      <div className={styles.thumbsTagContainer}>
        {thumbsDownOptions.map(option => (
          <Tag.CheckableTag
            key={option}
            checked={thumbsDownTags.includes(option)}
            onChange={() => handleThumbsDownTagClick(option)}
            className={`${styles.thumbsTag} ${thumbsDownTags.includes(option) ? styles.active : ''}`}
          >
            {option}
          </Tag.CheckableTag>
        ))}
      </div>
      <div style={{ padding: '0 20px 20px 20px' }}>
        <TextArea
          value={thumbsDownFeedback}
          onChange={e => setThumbsDownFeedback(e.target.value)}
          placeholder={t('请写下更多反馈内容')}
          rows={4}
          style={{ resize: 'none', height: 56, borderRadius: 6 }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div
        className={`copilotKitMessageControls ${isCurrentMessage ? 'currentMessage' : ''}`}
      >
        {isLastAssistantMessage && !inProgress && (
          <button
            className="copilotKitMessageControlButton"
            onClick={handleRegenerate}
            aria-label={labels.regenerateResponse}
            title={labels.regenerateResponse}
          >
            {icons.regenerateIcon}
            {t('重新生成')}
          </button>
        )}
        <div className='copilotKitMessageControlRight'>
          {onThumbsUp && (
            <button
              className={`copilotKitMessageControlButton ${feedbackStatus === Rating.useful ? 'feedbackActive' : ''}`}
              aria-label={labels.thumbsUp}
              title={labels.thumbsUp}
              onClick={handleThumbsUpClick}
            >
              {icons.thumbsUpIcon}
            </button>
          )}
          {onThumbsDown && (
            <button
              className={`copilotKitMessageControlButton ${feedbackStatus === Rating.unuseful ? 'feedbackActive' : ''}`}
              aria-label={labels.thumbsDown}
              title={labels.thumbsDown}
              onClick={handleThumbsDownClick}
            >
              {icons.thumbsDownIcon}
            </button>
          )}
        </div>
      </div>

      {/* 点赞反馈 Modal */}
      <Modal
        title={t('感谢您的反馈')}
        visible={thumbsUpVisible}
        onCancel={handleThumbsUpCancel}
        footer={[
          <Button
            key="submit"
            type="primary"
            onClick={handleThumbsUpConfirm}
            loading={thumbsUpLoading}
            disabled={thumbsUpTags.length === 0 && !thumbsUpFeedback.trim()}
          >
            {t('提交')}
          </Button>,
        ]}
        width={440}
        wrapClassName={styles.feedbackModal}
      >
        {thumbsUpContent}
      </Modal>

      {/* 点踩反馈 Modal */}
      <Modal
        title={t('感谢您的反馈')}
        visible={thumbsDownVisible}
        onCancel={handleThumbsDownCancel}
        footer={[
          <Button key="cancel" onClick={handleThumbsDownCancel}>
            {t('取消')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleThumbsDownConfirm}
            loading={thumbsDownLoading}
            disabled={thumbsDownTags.length === 0 && !thumbsDownFeedback.trim()}
          >
            {t('提交')}
          </Button>,
        ]}
        width={480}
        wrapClassName={styles.feedbackModal}
      >
        {thumbsDownContent}
      </Modal>
    </>
  );
};
